<template>
  <div class="kd-image-layer-settings">
    <div
      class="kd-layer-settings-unit"
      :class="{
        'kd-layer-settings-unit--disabled': !selectedLayerHasImage,
      }"
    >
      <div class="kd-layer-settings-unit__headline">Vergrößern</div>
      <kd-range-slider
        class="kd-layer-settings-unit__slider"
        :initial-value="+scale"
        :step="0.05"
        :min="0.1"
        :max="4"
        :show-labels="true"
        label-left="kleiner"
        label-center=""
        label-right="größer"
        @change="scaleSliderChanged"
      ></kd-range-slider>
    </div>
    <div
      class="kd-layer-settings-unit"
      :class="{
        'kd-layer-settings-unit--disabled': !selectedLayerHasImage,
      }"
    >
      <div class="kd-layer-settings-unit__headline">Drehen</div>
      <kd-range-slider
        class="kd-layer-settings-unit__slider"
        :initial-value="+rotation"
        :step="1"
        :min="-180"
        :max="180"
        label-left="links"
        label-center="normal"
        label-right="rechts"
        @change="rotationSliderChanged"
      ></kd-range-slider>
    </div>

    <div
      class="kd-layer-settings-unit"
      :class="{
        'kd-layer-settings-unit--hidden': !isInternetExplorer,
      }"
    >
      <div class="kd-layer-settings-unit__headline">Information</div>
      <p class="kd-layer-settings-unit__info">
        Leider unterstützt Ihr Browser nicht alle Funktionen des Grußkartendesigners. Für ein optimales Benutzertererlebnis verwenden Sie einen modernen Browser wie Chrome.
      </p>
    </div>

    <div
      class="kd-layer-settings-unit"
      :class="{
        'kd-layer-settings-unit--disabled': !selectedLayerHasImage,
        'kd-layer-settings-unit--hidden': isInternetExplorer,
      }"
    >
      <div
        class="kd-layer-settings-unit__headline kd-layer-settings-unit__headline--effects"
      >Effekte</div>
      <kd-image-effects
        :grayscaleActive="ACTIVE_LAYER.filterGrayscaleActive"
        :sepiaActive="ACTIVE_LAYER.filterSepiaActive"
      ></kd-image-effects>
    </div>

    <div
      class="kd-layer-settings-unit"
      :class="{
        'kd-layer-settings-unit--disabled': !selectedLayerHasImage,
        'kd-layer-settings-unit--hidden': isInternetExplorer,
      }"
    >
      <div class="kd-layer-settings-unit__headline">Kontrast</div>
      <kd-range-slider
        class="kd-layer-settings-unit__slider"
        :initial-value="+contrast"
        :step="0.05"
        :min="1"
        :max="2"
        :show-labels="true"
        label-left="normal"
        label-center="viel"
        label-right="sehr viel"
        @change="contrastSliderChanged"
      ></kd-range-slider>
    </div>

    <div
      class="kd-layer-settings-unit"
      :class="{
        'kd-layer-settings-unit--disabled': !selectedLayerHasImage,
        'kd-layer-settings-unit--hidden': isInternetExplorer,
      }"
    >
      <div class="kd-layer-settings-unit__headline">Helligkeit</div>
      <kd-range-slider
        class="kd-layer-settings-unit__slider"
        :initial-value="+brightness"
        :step="0.05"
        :min="0.2"
        :max="1.8"
        :show-labels="true"
        label-left="dunkler"
        label-center="normal"
        label-right="heller"
        @change="brightnessSliderChanged"
      ></kd-range-slider>
    </div>

    <div
      class="kd-layer-settings-unit kd-layer-settings-unit--reset-image-styles"
      :class="{
        'kd-layer-settings-unit--disabled': !selectedLayerHasImage,
      }"
      style="margin-bottom: 18px"
    >
      <kd-button-medium
        text="Bildstil zurücksetzen"
        @click.native="resetImageStyles"
      ></kd-button-medium>
    </div>

    <kd-image-dropzone></kd-image-dropzone>

  </div>
</template>

<script>
import * as log from 'loglevel';
import { mapGetters, mapMutations } from 'vuex';

import KdRangeSlider from '@/components/KdRangeSlider.vue';
import KdImageEffects from '@/components/KdImageEffects.vue';
import KdButtonMedium from '@/components/KdButtonMedium.vue';
import KdImageDropzone from '@/components/KdImageDropzone.vue';
import browserCheck from '@/services/browser-check';

import {
  ACTIVE_LAYER_ID,
  ACTIVE_LAYER,
} from '@/store/getter-types';

import {
  SET_CONTRAST_FOR_ACTIVE_LAYER,
  SET_BRIGHTNESS_FOR_ACTIVE_LAYER,
  SET_SCALE_FOR_ACTIVE_LAYER,
  SET_ROTATION_FOR_ACTIVE_LAYER,
  RESET_IMAGE_LAYER_STYLES,
} from '@/store/mutation-types';

const FILTER_CONTRAST_DEFAULT = 1;
const FILTER_BRIGHTNESS_DEFAULT = 1;
const TRANSFORM_SCALE_DEFAULT = 1;
const TRANSFORM_ROTATION_DEFAULT = 0;

export default {
  data() {
    return {
      minFontSize: 6,
      maxFontSize: 100,
      contrast: FILTER_CONTRAST_DEFAULT,
      brightness: FILTER_BRIGHTNESS_DEFAULT,
      scale: TRANSFORM_SCALE_DEFAULT,
      rotation: TRANSFORM_ROTATION_DEFAULT,
    };
  },
  /* eslint-disable no-prototype-builtins */
  computed: {
    ...mapGetters([
      ACTIVE_LAYER_ID,
      ACTIVE_LAYER,
    ]),
    // TODO: This was "ACTIVE_LAYER" before (not this.ACTIVE_LAYER, so not working),
    // so some code in here might be simplified. And it is so complex because
    // of this previous reactivity bug, caused by misspelling
    layer() { return this.ACTIVE_LAYER; },
    fontSize() { return this.layer ? this.layer.fontSize : 1; },
    initialContrast() { return this.layer && this.layer.hasOwnProperty('filterContrast') ? this.layer.filterContrast : FILTER_CONTRAST_DEFAULT; },
    initialBrightness() { return this.layer && this.layer.hasOwnProperty('filterBrightness') ? this.layer.filterBrightness : FILTER_BRIGHTNESS_DEFAULT; },
    initialScale() { return this.layer && this.layer.hasOwnProperty('transformScale') ? this.layer.transformScale : TRANSFORM_SCALE_DEFAULT; },
    initialRotate() { return this.layer && this.layer.hasOwnProperty('transformRotate') ? this.layer.transformRotate : TRANSFORM_ROTATION_DEFAULT; },
    currentImageUrlCss() {
      if (this.layer && this.layer.imageUrl) {
        return this.fullImageUrl(this.layer.imageUrl);
      }
      return '';
    },
    selectedLayerHasImage() {
      return this.layer && this.layer.imageUrl;
    },
    isInternetExplorer() {
      return browserCheck.isInternetExplorer();
    },
  },
  methods: {
    ...mapMutations([
      RESET_IMAGE_LAYER_STYLES,
    ]),
    fullImageUrl(imageUrl) {
      if (imageUrl) {
        // -> xyz.hach.de/uploads/1234.svg / localhost:3000/uploads/1234.svg
        return process.env.VUE_APP_IMAGE_SERVER + imageUrl;
      }
      return '';
    },
    contrastSliderChanged(value) {
      this.$store.commit(SET_CONTRAST_FOR_ACTIVE_LAYER, value);
    },
    brightnessSliderChanged(value) {
      this.$store.commit(SET_BRIGHTNESS_FOR_ACTIVE_LAYER, value);
    },
    scaleSliderChanged(value) {
      this.$store.commit(SET_SCALE_FOR_ACTIVE_LAYER, value);
    },
    rotationSliderChanged(value) {
      this.$store.commit(SET_ROTATION_FOR_ACTIVE_LAYER, value);
    },
    updateValues() {
      this.contrast = this.initialContrast;
      this.brightness = this.initialBrightness;
    },
    resetImageStyles() {
      this.RESET_IMAGE_LAYER_STYLES();
    },
  },
  watch: {
    initialContrast() {
      this.contrast = this.ACTIVE_LAYER.filterContrast;
    },
    initialBrightness() {
      this.brightness = this.ACTIVE_LAYER.filterBrightness;
    },
    initialScale() {
      log.debug('Scale changed in active layer');
      this.scale = this.ACTIVE_LAYER.transformScale;
    },
    initialRotate() {
      log.debug('Rotation changed in active layer');
      this.rotation = this.ACTIVE_LAYER.transformRotate;
    },
  },
  mounted() { this.updateValues(); },
  updated() { this.updateValues(); },
  components: {
    KdRangeSlider,
    KdImageEffects,
    KdButtonMedium,
    KdImageDropzone,
  },
};
</script>

<style lang="scss">
@import '@/scss/kd-layer-settings-unit.scss';

/*
 * overwrites of base styles
 */

.kd-layer-settings-unit__headline {
  &--effects {
    margin-bottom: 14px;
  }
}

.kd-layer-settings-unit {
  &--reset-image-styles {
    margin-bottom: 18px;

    @media all and (max-height: 680px) {
      margin-bottom: 12px;
    }
  }
}
</style>
